import { generatePath } from 'react-router-dom';

import type {
  EntityInvestmentBoardEntryDetailDto,
  EntityInvestmentBoardTimelineItemDto,
} from '@/api/investing-board/types';
import type { PipelineCardDetail } from '@/pages/pipelines/common/types';
import { getPipelinePersonLabel } from '@/pages/pipelines/common/utils/mapping';
import { PagePaths } from '@/routes/page-paths';
import { formatDayMonthYear } from '@/utils/format-date';
import { formatMoney } from '@/utils/numbers/formatters';
import { pluralize } from '@/utils/pluralize';

import { getInvestingBoardStageLabel } from './board-stages';

const INVESTING_BOARD_PIPELINE = 'investing-board';
const UNNAMED_FUND = 'Unnamed fund';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getFundLabel = (fund: { name: string | null }): string => fund.name?.trim() || UNNAMED_FUND;

const getPledgedValue = (pledged: EntityInvestmentBoardEntryDetailDto['pledged']): string | null =>
  pledged ? formatMoney(Number(pledged.amount), pledged.denomination) : null;

/**
 * Dated from the most recent move that reached the current stage rather than from
 * the entry's `updatedAt`, which any edit touches. A card can re-enter a stage, so
 * the latest matching move is the one that is still running.
 */
const getStageSinceValue = (
  timeline: EntityInvestmentBoardTimelineItemDto[],
  stage: string,
  now: Date,
): string | null => {
  const reachedAt = timeline.reduce<string | null>((latest, item) => {
    if (item.stage !== stage) return latest;
    if (!latest) return item.reachedAt;

    return Date.parse(item.reachedAt) > Date.parse(latest) ? item.reachedAt : latest;
  }, null);

  if (!reachedAt) return null;

  const elapsed = now.getTime() - Date.parse(reachedAt);
  if (!Number.isFinite(elapsed)) return null;

  const days = Math.max(0, Math.floor(elapsed / DAY_IN_MS));

  return `${formatDayMonthYear(reachedAt)} (${days === 0 ? 'today' : pluralize(days, 'day')})`;
};

/** Keeps the backend's own wording for a stage this build has no label for yet. */
const getTimelineLabel = (item: EntityInvestmentBoardTimelineItemDto): string => {
  const label = getInvestingBoardStageLabel(item.stage);

  return label === item.stage ? item.label : label;
};

export const mapEntityEntryDetailToPipelineCardDetail = (
  detail: EntityInvestmentBoardEntryDetailDto,
  now: Date = new Date(),
): PipelineCardDetail => {
  const investorLabel = getPipelinePersonLabel(detail.investor);
  const fundLabel = getFundLabel(detail.fund);

  return {
    id: detail.id,
    pipeline: INVESTING_BOARD_PIPELINE,
    stage: detail.stage,
    active: true,
    title: investorLabel ?? fundLabel,
    subtitleLines: [fundLabel],
    badges: [],
    indicators: [],
    timer: null,
    lastStageChangedAt: detail.updatedAt,
    entityRefs: { fundId: detail.fund.id, investorUserId: detail.investor.id },
    headerActions: getEntityDetailActions(detail),
    sections: [
      {
        key: 'summary',
        title: 'Summary',
        rows: [
          { key: 'stage', label: 'Stage', value: getInvestingBoardStageLabel(detail.stage) },
          {
            key: 'stageSince',
            label: 'In this stage since',
            value: getStageSinceValue(detail.timeline, detail.stage, now),
          },
          { key: 'fund', label: 'Fund', value: fundLabel },
          { key: 'pledged', label: 'Pledged', value: getPledgedValue(detail.pledged) },
          {
            key: 'followingFund',
            label: 'Following fund',
            value: detail.followingFund ? 'Yes' : 'No',
          },
        ],
      },
    ],
    timeline: detail.timeline.map((item) => ({
      stage: item.stage,
      label: getTimelineLabel(item),
      reachedAt: item.reachedAt,
      highlighted: true,
      source: null,
      triggerEventName: null,
      note: null,
    })),
  };
};

type PipelineDetailAction = PipelineCardDetail['headerActions'][number];

const getEntityDetailActions = (
  detail: EntityInvestmentBoardEntryDetailDto,
): PipelineDetailAction[] => [
  {
    key: 'viewFund',
    label: 'View fund',
    href: generatePath(PagePaths.fundDashboardId, { id: detail.fund.id }),
    placement: 'primary',
  },
];
